import { useRef, useState } from "react";
import { api, ApiError } from "../../lib/api";
import type { ExtractedReport, LolerGmailMessage, ReportCompleteness } from "../../lib/api";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { Button } from "../../components/Button";
import { formatDate } from "../../lib/format";

// Session BAD: "Add LOLER report" on a lift and on a job. The office names no lift (this is opened FROM one) and picks the PDF
// from where it already is: an email in the info@ mailbox (searched here, no Drive step) or a file dropped on the box. The AI reads
// it into an editable form; NOTHING is recorded until the office presses Add report, the examination date is the report's own or
// typed (never filled in for them), and the PDF is filed to the lift's LOLER folder by the server. The same office route as the New
// report form records it, so the same-day guard, the provenance key and the lift's LOLER clock all apply.

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-body";
const tabCls = (on: boolean) => `rounded-ele px-3 py-1.5 font-menu text-xs tracking-wide ${on ? "bg-ele-green text-ele-dark" : "border border-ele-purple-accent text-ele-body hover:text-ele-strong"}`;

type Source = { kind: "gmail"; messageId: string; filename: string } | { kind: "file"; file: File };

export function AddLolerReportModal({ assetId, liftLabel, onClose, onDone }: { assetId: string; liftLabel: string; onClose: () => void; onDone: (reportId: string) => void }) {
  const [tab, setTab] = useState<"gmail" | "file">("gmail");
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState<LolerGmailMessage[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [source, setSource] = useState<Source | null>(null);
  const [reading, setReading] = useState(false);
  const [notReport, setNotReport] = useState(false);
  const [extracted, setExtracted] = useState<ExtractedReport | null>(null);
  const [form, setForm] = useState({ inspectorName: "", examinationDate: "", nextExaminationDue: "", reportRef: "", completeness: "full" as ReportCompleteness });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  function fillFrom(x: ExtractedReport) {
    setExtracted(x);
    setForm({
      inspectorName: x.header.inspectorName ?? "",
      examinationDate: x.header.examinationDate ?? "",
      nextExaminationDue: x.header.nextExaminationDue ?? "",
      reportRef: x.header.reportRef ?? "",
      completeness: (x.header.completeness ?? "full") as ReportCompleteness,
    });
  }

  async function search() {
    setSearching(true);
    setErr(null);
    try {
      const r = await api.loler.gmailMessages(query.trim());
      setMessages(r.messages);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not search the mailbox.");
    } finally {
      setSearching(false);
    }
  }

  async function pickEmail(messageId: string, filename: string) {
    setSource({ kind: "gmail", messageId, filename });
    setReading(true);
    setErr(null);
    setNotReport(false);
    try {
      const r = await api.loler.gmailExtract(messageId, filename);
      setNotReport(!r.isLolerReport);
      fillFrom({ header: r.header, defects: r.defects });
    } catch (e) {
      // The form still opens empty: the AI is an enhancement, never the only way in.
      setErr((e instanceof ApiError ? e.message || e.code : "Could not read that PDF.") + " You can type the details from the PDF.");
      fillFrom({ header: {}, defects: [] });
    } finally {
      setReading(false);
    }
  }

  async function pickFile(file: File) {
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setErr("The report must be a PDF.");
      return;
    }
    setSource({ kind: "file", file });
    setReading(true);
    setErr(null);
    setNotReport(false);
    try {
      fillFrom(await api.loler.extractUpload(file));
    } catch (e) {
      setErr((e instanceof ApiError ? e.message || e.code : "Could not read that PDF.") + " You can type the details from the PDF.");
      fillFrom({ header: {}, defects: [] });
    } finally {
      setReading(false);
    }
  }

  const blocked = !form.examinationDate ? "Enter the examination date from the report." : null;

  async function add() {
    if (!source || blocked) return;
    setBusy(true);
    setErr(null);
    try {
      const report = await api.loler.createReport({
        assetId,
        inspectorName: form.inspectorName.trim() || undefined,
        examinationDate: form.examinationDate,
        nextExaminationDue: form.nextExaminationDue || undefined,
        reportRef: form.reportRef.trim() || undefined,
        completeness: form.completeness,
        defects: (extracted?.defects ?? []).map((d) => ({ description: d.description, category: d.category, deadline: d.deadline ?? null })),
        ...(source.kind === "gmail" ? { sourceMessageId: source.messageId, sourceFilename: source.filename } : {}),
      });
      // Filing the PDF is non-fatal: the report exists either way, and the lift's clock has already moved.
      try {
        if (source.kind === "gmail") await api.loler.fileGmailAttachment(report.id, { gmailMessageId: source.messageId, gmailAttachmentId: "", filename: source.filename });
        else await api.loler.uploadReportPdf(report.id, source.file);
      } catch {
        // said on the report page; the office can add the PDF there
      }
      onDone(report.id);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not add the report.");
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Add LOLER report" onClose={onClose} width="max-w-[640px]" margin="my-8" panelClassName="p-6">
      <h2 className="mb-1 font-heading text-2xl tracking-wide">Add LOLER report</h2>
      <p className="mb-3 text-sm text-ele-body">
        For <span className="text-ele-strong">{liftLabel}</span>. Pick the examination report PDF from an email or drop the file here. Nothing is recorded until you press Add report.
      </p>

      {!extracted ? (
        <>
          <div className="mb-3 flex gap-2" role="tablist">
            <button type="button" role="tab" aria-selected={tab === "gmail"} className={tabCls(tab === "gmail")} onClick={() => setTab("gmail")}>
              From an email
            </button>
            <button type="button" role="tab" aria-selected={tab === "file"} className={tabCls(tab === "file")} onClick={() => setTab("file")}>
              Drop a PDF
            </button>
          </div>

          {tab === "gmail" ? (
            <div>
              <div className="flex gap-2">
                <input className={inputCls} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search the mailbox: a site name, a sender, a word from the subject" onKeyDown={(e) => { if (e.key === "Enter") void search(); }} aria-label="Search the mailbox" />
                <Button type="button" onClick={() => void search()} disabled={searching}>
                  {searching ? "Searching" : "Search"}
                </Button>
              </div>
              <p className="mt-1 text-xs text-ele-body">Shows recent emails in the info@ mailbox that carry a PDF. Leave the box empty to see the latest.</p>
              {messages && messages.length === 0 ? <p className="mt-3 text-sm text-ele-body">No emails with a PDF matched.</p> : null}
              <ul className="mt-3 max-h-80 space-y-2 overflow-y-auto">
                {(messages ?? []).map((m) => (
                  <li key={m.messageId} className="rounded-ele border border-ele-purple-accent bg-ele-surface p-2 text-sm">
                    <p className="truncate font-heading text-ele-strong">{m.subject || "(no subject)"}</p>
                    <p className="text-xs text-ele-body">
                      {m.fromName || m.from} · {formatDate(m.date)}
                    </p>
                    <ul className="mt-1 space-y-1">
                      {m.attachments.map((a) => (
                        <li key={a.filename} className="flex flex-wrap items-center justify-between gap-2">
                          <span className="truncate text-xs text-ele-body">{a.filename}{a.size != null ? ` (${Math.round(a.size / 1024)} KB)` : ""}</span>
                          <span className="flex gap-3">
                            <a href={api.loler.gmailAttachmentHref(m.messageId, "", a.filename)} target="_blank" rel="noreferrer" className="font-menu text-xs tracking-wide text-ele-blue hover:underline">
                              View PDF
                            </a>
                            <button type="button" onClick={() => void pickEmail(m.messageId, a.filename)} disabled={reading} className="font-menu text-xs tracking-wide text-ele-green hover:underline disabled:opacity-50">
                              Use this PDF
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const f = e.dataTransfer.files?.[0];
                if (f) void pickFile(f);
              }}
              className={`rounded-ele border-2 border-dashed p-8 text-center ${dragOver ? "border-ele-green bg-ele-inset" : "border-ele-purple-accent"}`}
            >
              <p className="text-sm text-ele-body">Drag the report PDF here</p>
              <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="sr-only" aria-label="Choose the report PDF" onChange={(e) => { const f = e.target.files?.[0]; if (f) void pickFile(f); }} />
              <Button type="button" className="mt-3" onClick={() => fileRef.current?.click()}>
                Choose a file
              </Button>
            </div>
          )}
          {reading ? <p className="mt-3 text-sm text-ele-body">Reading the PDF...</p> : null}
        </>
      ) : (
        <div className="space-y-3">
          <p className="truncate text-xs text-ele-body">{source?.kind === "file" ? source.file.name : source?.filename}</p>
          {notReport ? (
            <p role="alert" className="rounded-ele border border-ele-amber-line bg-ele-surface-soft px-3 py-2 text-xs text-[var(--ele-amber-text)]">
              This PDF does not look like a LOLER examination report. Check it before adding anything.
            </p>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls} htmlFor="alr-exam">Examination date</label>
              <input id="alr-exam" type="date" className={inputCls} value={form.examinationDate} onChange={(e) => setForm({ ...form, examinationDate: e.target.value })} />
              {!extracted.header.examinationDate ? <p className="mt-1 text-xs text-[var(--ele-amber-text)]">The report states no readable date. Type it from the PDF.</p> : null}
            </div>
            <div>
              <label className={labelCls} htmlFor="alr-next">Next examination due</label>
              <input id="alr-next" type="date" className={inputCls} value={form.nextExaminationDue} onChange={(e) => setForm({ ...form, nextExaminationDue: e.target.value })} />
              <p className="mt-1 text-xs text-ele-body">Leave blank if the report states none; six months on is used.</p>
            </div>
            <div>
              <label className={labelCls} htmlFor="alr-body">Examining body</label>
              <input id="alr-body" className={inputCls} value={form.inspectorName} onChange={(e) => setForm({ ...form, inspectorName: e.target.value })} />
            </div>
            <div>
              <label className={labelCls} htmlFor="alr-ref">Report reference</label>
              <input id="alr-ref" className={inputCls} value={form.reportRef} onChange={(e) => setForm({ ...form, reportRef: e.target.value })} />
            </div>
          </div>
          <p className="text-xs text-ele-body">
            {extracted.defects.length === 0 ? "No defects were read from the report." : `${extracted.defects.length} defect${extracted.defects.length === 1 ? "" : "s"} read from the report will be recorded; edit them on the report page.`}
          </p>
        </div>
      )}

      {err ? (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-danger-fill px-3 py-2 text-sm text-ele-danger">
          {err}
        </p>
      ) : null}

      <div className={`gap-2 ${MODAL_ACTIONS}`}>
        <button onClick={onClose} disabled={busy} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>
          Cancel
        </button>
        {extracted ? (
          <>
            <button onClick={() => { setExtracted(null); setSource(null); setErr(null); }} disabled={busy} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>
              Pick another PDF
            </button>
            <Button type="button" onClick={() => void add()} disabled={busy || blocked != null} className={MODAL_ACTION_BTN}>
              {busy ? "Adding" : "Add report"}
            </Button>
          </>
        ) : null}
      </div>
      {extracted && blocked ? <p className="mt-2 text-right text-xs text-ele-body">{blocked}</p> : null}
    </ModalShell>
  );
}
